//initial redux
import { put, call, takeEvery, fork, all, take, select, takeLatest } from "redux-saga/effects";
import * as router from "react-router-redux";
import { eventChannel, delay } from "redux-saga";
import moment from "moment-timezone";
import pointsservice from "../../../services/points";
import ticketservice from "../../../services/ticket";
import facilityservice from "../../../services/facility";
import {types as sitetreetypes} from "../../../services/SiteTree/redux";
import {types as livetypes} from "../../../ui/Live/redux";
import takeLeading from "../../../utils/takeLeading";

const LIVE_DATA_INTERVAL = 5000;

const initialState = {
    filters: ["kWh", "Per m²", "Per Ticket", "Per Seat"],
    selectedFilter: "kWh",
    monthName: moment().format("MMMM"),
    siteInfo: null,
    chart: null,
    monthlyUsage: [],
    monthlyTickets: [],
    onPage: false,
	isBusy: false,
	errors: [],
	hasError: false,
    isProcessing: false,
};

/////////
//types
export const types = {
    SELECT_FILTER: "components/monthlyusagecomparisonchart/SELECT_FILTER",
    START_LIVE_DATA: "components/monthlyusagecomparisonchart/START_LIVE_DATA",
    STOP_LIVE_DATA: "components/monthlyusagecomparisonchart/STOP_LIVE_DATA",
    UPDATE_LIVE_DATA: "components/monthlyusagecomparisonchart/UPDATE_LIVE_DATA",
    LOAD_SITE_DATA: "components/monthlyusagecomparisonchart/LOAD_SITE_DATA",
    SET_SITE_INFO: "components/monthlyusagecomparisonchart/SET_SITE_INFO",
    SET_MONTHLY_USAGE: "components/monthlyusagecomparisonchart/SET_MONTHLY_USAGE",
    SET_MONTHLY_TICKETS: "components/monthlyusagecomparisonchart/SET_MONTHLY_TICKETS",
    SET_IS_PROCESSING: "components/monthlyusagecomparisonchart/SET_IS_PROCESSING",
    RESET_DATA: "components/monthlyusagecomparisonchart/RESET_DATA"
};

///////////
//reducers
export const reducers = {
	reducer(state = initialState, action) {
		switch (action.type) {
            case types.SELECT_FILTER: {
                return {...state, selectedFilter: action.payload.filter};
            }
            case types.START_LIVE_DATA: {
                return {...state, onPage: true, chart: action.payload.chart};
            }
            case types.RESET_DATA:{
                return {...state, onPage: false, chart: null, monthlyCosts: [], monthlyTickets: []};
            }
            case types.UPDATE_LIVE_DATA:{
                return state;
            }
            case types.LOAD_SITE_DATA:{
                return state;
            }
            case types.SET_SITE_INFO: {
                return {...state, siteInfo: action.payload.siteInfo};
            }
            case types.SET_MONTHLY_USAGE:{
                return {...state, monthlyUsage: action.payload.monthlyUsage}
            }
            case types.SET_MONTHLY_TICKETS:{
                return {...state, monthlyTickets: action.payload.monthlyTickets};
            }
            case livetypes.SET_LIVE_PAGE_CONFIGURATION:{
                let filters = state.filters.slice();
                if (!action.payload.conf.ticketsIncluded) {
                    filters = filters.filter(x => x != "Per Ticket" && x != "Per Seat");
                }
                if (!action.payload.conf.siteAreaAvailable) {
                    filters = filters.filter(x => x != "Per m²");
                }
                return {...state, filters: filters };
            }
            case types.SET_IS_PROCESSING: {
                return {...state, isProcessing: action.payload }
            }
			default:
				return state;
		}
	}
};

function* getUsage(period, viewId){
    if(!viewId) {
        return {usage: 0, year: period.year};
    }

    const usageData = yield call(pointsservice.TrendableDataGetData, viewId, period.fromDate,
                            (period.isToday ? 'now' : period.toDate), null, null, "Monthly", true, true);
    let usage = 0;
    if(usageData && usageData.length){
        usageData.forEach(s => {
            usage += s.value;
        });
    }
    return {usage: usage, year: period.year};
}


function getView(flattenedSiteTree, rootView, paths){
	let result = null;

	let wkView = rootView;
	if(flattenedSiteTree && rootView && paths && paths.length && flattenedSiteTree.Roots.length){
		for(var ii = 0; ii < paths.length; ii++){
			let wkPath = paths[ii];
         let nextView = null;
         
         const childs = flattenedSiteTree.GetChildrenFor(wkView);

         for (let index = 0; index < childs.length; index++) {
            const childView = childs[index];
            if(childView.name === wkPath){
               nextView = childView;
               break;
            }
         }

			if(!nextView){
				break;
			}else{
				wkView = nextView;
			}
			if(ii === paths.length - 1){
				result = wkView;
			}
		}
	}

	return result;
}

//////////
//sagas
export const sagas = {
	*rootSaga() {
		yield all([
            this.startLiveDataInterval(),
            this.updateLiveData(),
            this.loadSiteData(),
            this.redrawChart(),
            this.clearData(),
            this.stopLiveData()
        ]);
    },
    *startLiveDataInterval(){
        yield takeLatest(types.START_LIVE_DATA, function*({payload}){
            for(;;){
                const onPage = yield select(x => x.monthlyusagecomparisonchart.onPage);
                if(onPage){
                    yield put(actions.updateLiveData());
                }else{
                    return;
                }
                yield delay(LIVE_DATA_INTERVAL);
            }
        });
    },
    *clearData(){
        yield takeLatest([sitetreetypes.SET_CURRENT_SITE, sitetreetypes.ON_LOAD], function*({payload}){
            const onPage = yield select(x => x.live.onPage);
            if(!onPage){
                return;
            }
            const chart = yield select(x => x.monthlyusagecomparisonchart.chart);
            if(chart && chart.options){
                chart.series[0].update({data:[]});
            }
            yield put(actions.setSiteInfo(null));
            yield put(actions.setMonthlyUsage());
        });
    },
    *updateLiveData(){
        yield takeLeading([types.UPDATE_LIVE_DATA, sitetreetypes.SET_CURRENT_SITE, sitetreetypes.ON_LOAD], function*(){
            const onPage = yield select(x => x.live.onPage);
            const isProcessing = yield select(x => x.monthlyusagecomparisonchart.isProcessing);
            if(!onPage && !isProcessing){
                return;
            }
            yield put(actions.setIsProcessing(true));

            const livePageConf = yield select(x => x.live.livePageConf);
            const currentSite = yield select(x => x.sitetree.currentSite);
            const flattenedSiteTree = yield select(x => x.sitetree.flattenedSiteTree);
            if(!currentSite || !flattenedSiteTree){
                yield put(actions.setMonthlyUsage())
                return;
            }       
            let utcOffset = 0;
            if(currentSite && currentSite.momentTimezone){
                utcOffset = moment().tz(currentSite.momentTimezone).utcOffset();
            }
            let fromDate = moment().utc().add(utcOffset, 'm').startOf('month');
            let toDate = moment().utc().add(utcOffset, 'm');

            let year = Number(toDate.format("YYYY"));
            let periods = [];
            for(var ii = 5; ii > 0; ii--){
                periods.push({
                    year: year - ii,
                    fromDate: moment(fromDate).subtract(ii, 'years'),
                    toDate: moment(toDate).subtract(ii, 'years'),
                    isToday: false
                });
            }
            periods.push({
                year: year,
                fromDate: fromDate,
                toDate: toDate,
                isToday: true
            });
            const wkView = getView(flattenedSiteTree, currentSite, ["Meters", "Main"]);

            if (livePageConf.ticketsIncluded) {
                const [monthlyTicketsResponse] = yield all([periods.map((period) => call(ticketservice.GetTicketsSold, [currentSite.name], period.fromDate, period.toDate))]);
                let monthlyTickets = [];
                for(var ii = 0; ii < periods.length; ii++){
                    let year = periods[ii].year;
                    let tickets = 0;
                    if(monthlyTicketsResponse && monthlyTicketsResponse[ii]){
                        let propName = "site_" + currentSite.name.substring("Site_".length);
                        tickets = monthlyTicketsResponse[ii][propName];
                        tickets = tickets ? tickets : 0;
                    }
                    monthlyTickets.push({
                        year: year,
                        tickets: tickets,
                    });
                }
                yield put(actions.setMonthlyTickets(monthlyTickets));
            }
            
            if(wkView) {
                const [monthlyUsage] = yield all([periods.map((period) => call(getUsage, period, wkView.objectID))]);
                yield put(actions.setMonthlyUsage(monthlyUsage));
            } else {
                yield put(actions.setMonthlyUsage());
                console.error("no wkView was found for MonthlyUsageComparisonChart, which means there is no Meters -> Main node for current site");
            }
            yield put(actions.setIsProcessing(false));
        });
    },
    *loadSiteData(){
        yield takeLatest([types.START_LIVE_DATA, sitetreetypes.SET_CURRENT_SITE, sitetreetypes.ON_LOAD], function*(){
            const onPage = yield select(x => x.live.onPage);
            if(!onPage){
                return;
            }
            const currentSite = yield select(x => x.sitetree.currentSite);
            let newSiteInfo = null;
            if(currentSite){
                newSiteInfo = yield call(facilityservice.GetFacilityInfo, currentSite.name);
            }
            yield put(actions.setSiteInfo(newSiteInfo));
        });
    },
    *redrawChart(){
        yield takeLatest([types.SET_MONTHLY_USAGE, types.SELECT_FILTER, types.SET_SITE_INFO], function*(){
            const selectedFilter = yield select(x => x.monthlyusagecomparisonchart.selectedFilter);
            const siteInfo = yield select(x => x.monthlyusagecomparisonchart.siteInfo);
            const chart = yield select(x => x.monthlyusagecomparisonchart.chart);
            const monthlyUsage = yield select(x => x.monthlyusagecomparisonchart.monthlyUsage);
            const monthlyTickets = yield select(x => x.monthlycostcomparisonchart.monthlyTickets);
            if(chart && chart.options && selectedFilter && monthlyUsage){
                let multiplier = 1;
                switch(selectedFilter){
                    case "kWh":
                        multiplier = 1;
                        break;
                    case "Per m²":
                        multiplier = siteInfo != null ? (siteInfo.area ? (1 / siteInfo.area) : 0) : 0;
                        break;
                    case "Per Ticket":
                        multiplier = 1;
                        break;
                    case "Per Seat":
                        multiplier = siteInfo != null ? (siteInfo.totalSeats ? (1 / siteInfo.totalSeats) : 0) : 0;
                        break;
                    default:
                        multiplier = 1;
                        break;
                }
                let newData = [];
                let oldData = chart.series[0].data;
                for(var ii = 0; ii < monthlyUsage.length; ii++){
                    let newDataEntry = {};
                    newDataEntry.name = monthlyUsage[ii].year;
                    if(selectedFilter === "Per Ticket"){
                        let monthlyTicket = monthlyTickets.find((s) => s.year === monthlyUsage[ii].year);
                        if(monthlyTicket){
                            multiplier = monthlyTicket.tickets ? (1 / monthlyTicket.tickets) : 0;
                        }else{
                            multiplier = 0;
                        }
                    }
                    let newDataEntryValue = (Math.round(monthlyUsage[ii].usage * multiplier * 100)) / 100;
                    if((!newDataEntryValue && multiplier !== 0)  && oldData && oldData.length && oldData[ii]){
                        newDataEntryValue = oldData[ii].y;
                    }
                    newDataEntry.y = newDataEntryValue;
                    if(ii === monthlyUsage.length - 1){
                        newDataEntry.color = "#2D9BB1";
                    }
                    newData.push(newDataEntry);
                }
                chart.series[0].update({
                    data: newData,
                });
                if(chart && chart.options){
                    chart.reflow();
                    chart.redraw();
                }
            }else if(chart){
                chart.series[0].update({data: []});
                chart.reflow();
                chart.redraw();
            }
        });
    },
    *stopLiveData() {
        yield takeLatest([types.STOP_LIVE_DATA], function*() {
            const chart = yield select(x => x.monthlyusagecomparisonchart.chart);
            if(chart && chart.options){
                chart.series[0].update({data:[]});
                chart.reflow();
                chart.redraw();
            }
            yield put(actions.resetData());
        });
    }
};

////////
//actions
export const actions = {
    selectFilter(filter){
        return {
            type: types.SELECT_FILTER,
            payload: {filter}
        };
    },
    startLiveData(chart) {
        return {
           type: types.START_LIVE_DATA,
           payload: {chart}
        };
     },
     stopLiveData() {
        return {
           type: types.STOP_LIVE_DATA,
           payload: {}
        };
     },
     updateLiveData(){
		return {
			type: types.UPDATE_LIVE_DATA,
			payload: {},
		};
    },
    loadSiteData(){
        return {
            type: types.LOAD_SITE_DATA,
            payload: {},
        };
    },
    setSiteInfo(siteInfo){
        return {
            type: types.SET_SITE_INFO,
            payload: {siteInfo}
        };
    },
    redrawChart(){
        return {
            type: types.REDRAW_CHART,
            payload: {}
        };
    },
    setMonthlyUsage(monthlyUsage){
        return {
            type: types.SET_MONTHLY_USAGE,
            payload: {monthlyUsage}
        };
    },
    setMonthlyTickets(monthlyTickets){
        return {
            type: types.SET_MONTHLY_TICKETS,
            payload: {monthlyTickets}
        };
    },
    setIsProcessing(payload) {
        return {
            type: types.SET_IS_PROCESSING,
            payload
        }
    },
    resetData(){
        return {
            type: types.RESET_DATA
        }
    }
};


// WEBPACK FOOTER //
// ./src/components/LiveComponents/MonthlyUsageComparisonChart/redux.js